import {
  forwardRef,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type MutableRefObject,
  type ReactNode,
  type Ref,
} from 'react'
import classNames from 'classnames'
import { TEXT_FIELD_LIMIT_WARNING_MS } from 'shared/ui/TextField/ActionsMain/TextFieldHighlightLimit'
import { toastStore } from 'shared/ui/Toast'
import { Error } from 'shared/ui/Error'
import { type ILabelProps, Label } from 'shared/ui/Label/Label'
import { Warning } from 'shared/ui/Warning'
import { Textarea } from 'shared/ui/Textarea/Textarea'
import styles from './styles.module.scss'

export type IErrorType = 'toast' | 'bottomText'

const resizeTextareaByScrollHeight = (el: HTMLTextAreaElement | null) => {
  if (!el) return
  el.style.height = 'auto'
  el.style.height = `${el.scrollHeight}px`
}

export type ITextareaWithLabelProps = {
  name?: string
  placeholder?: string
  labelProps: ILabelProps
  error?: string | null
  value: string
  limit?: number
  minLimit?: number
  rows?: number
  autoResize?: boolean
  errorType?: IErrorType
  className?: string
  onBlur?: (value: string) => void
  onChange: (value: string) => void
  classNameTextarea?: string
  shouldResetErrors?: boolean
  validationMessage?: string
  bottomNode?: {
    leftNode?: ReactNode
    rightNode?: ReactNode
  }
}

export const TextareaWithLabelRef = (
  {
    name = 'message',
    placeholder,
    labelProps,
    error,
    value,
    limit,
    minLimit,
    rows = 4,
    autoResize = false,
    errorType = 'bottomText',
    className,
    onBlur,
    onChange,
    classNameTextarea,
    shouldResetErrors = false,
    bottomNode,
    validationMessage,
  }: ITextareaWithLabelProps,
  ref: Ref<HTMLTextAreaElement>
) => {
  const isDirty = useRef(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const limitWarningTimerRef = useRef<number>(-1)
  const limitToastArmedRef = useRef(true)
  const [limitWarningVisible, setLimitWarningVisible] = useState(false)
  const [minLengthError, setMinLengthError] = useState('')

  const setRef = (el: HTMLTextAreaElement | null) => {
    const mutableTextareaRef = textareaRef as MutableRefObject<HTMLTextAreaElement | null>
    mutableTextareaRef.current = el
    if (ref) {
      if (typeof ref === 'function') ref(el)
      else (ref as MutableRefObject<HTMLTextAreaElement | null>).current = el
    }
  }

  useLayoutEffect(() => {
    if (autoResize) resizeTextareaByScrollHeight(textareaRef.current)
  }, [autoResize, value])

  const getLimitLengthMessage = () => {
    if (limit === null || limit === undefined) return null
    return `Cannot be longer than ${limit} characters`
  }

  const handleChange = (value: string) => {
    isDirty.current = true
    onChange(value)
    setMinLengthError('')

    const overLimit = limit !== null && limit !== undefined && value.length > limit
    const limitLengthMessage = getLimitLengthMessage()

    if (overLimit) {
      if (errorType === 'bottomText') {
        setLimitWarningVisible(true)
        window.clearTimeout(limitWarningTimerRef.current)
        limitWarningTimerRef.current = window.setTimeout(() => {
          setLimitWarningVisible(false)
        }, TEXT_FIELD_LIMIT_WARNING_MS)
      }

      if (errorType === 'toast' && limitLengthMessage && limitToastArmedRef.current) {
        toastStore.add({
          title: limitLengthMessage,
          type: 'error',
        })
        limitToastArmedRef.current = false
      }
    } else {
      window.clearTimeout(limitWarningTimerRef.current)
      limitToastArmedRef.current = true
      if (errorType === 'bottomText') {
        setLimitWarningVisible(false)
      }
    }
  }

  useEffect(() => {
    if (shouldResetErrors) {
      setMinLengthError('')
      isDirty.current = true
    }
  }, [shouldResetErrors])

  const handleBlur = (value: string) => {
    onBlur?.(value)

    if (minLimit && value.length < minLimit) {
      setMinLengthError(validationMessage || `The ${name} must be at least ${minLimit} characters`)
    }
  }

  useEffect(() => {
    return () => {
      window.clearTimeout(limitWarningTimerRef.current)
    }
  }, [])

  const showLimitWarning = errorType === 'bottomText' && limitWarningVisible
  const isShowBottomError = error || minLengthError

  return (
    <div className={classNames(styles.textareaWrap, className)}>
      {labelProps.label && <Label marginBottom={4} {...labelProps} />}

      <Textarea
        ref={setRef}
        rows={rows}
        placeholder={placeholder}
        error={!!error || !!minLengthError}
        limitWarning={showLimitWarning && !error && !minLengthError}
        value={value}
        onBlur={handleBlur}
        onChange={handleChange}
        className={classNameTextarea}
      />

      {isShowBottomError && <Error type='text' error={error || minLengthError} size='small' />}
      {showLimitWarning && (
        <Warning text={getLimitLengthMessage() ?? ''} className={styles.warning} />
      )}
      {bottomNode && (
        <div className={styles.bottomNode}>
          {bottomNode.leftNode} {bottomNode.rightNode}
        </div>
      )}
    </div>
  )
}

export const TextareaWithLabel = forwardRef(TextareaWithLabelRef)
